/**
 * Reusable Map Component
 *
 * Single Responsibility: Renders a Google Map with markers
 * Open/Closed: Extensible through props and configuration
 * Dependency Inversion: Depends on abstractions (interfaces) not concrete implementations
 */

'use client';

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';
import { AdvancedMarker } from './AdvancedMarker';
import { ReusableMapProps, MapMarker } from './types';
import { defaultMapOptions } from './MapStyles';
import { MapBoundsCalculator } from './MapBoundsCalculator';
import { createExperienceMarkerElement } from './ExperienceMarker';
import {
  backgroundColors,
  typographyColors,
  strokeColors,
  borders,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';

/**
 * Default center (center of India)
 */
const DEFAULT_CENTER = {
  lat: 20.5937,
  lng: 78.9629,
};

/**
 * Default zoom level
 */
const DEFAULT_ZOOM = 10;

/**
 * Default container height
 */
const DEFAULT_HEIGHT = '384px';

/**
 * Reusable Map Component
 *
 * Follows SOLID principles:
 * - Single Responsibility: Only renders map with markers
 * - Open/Closed: Extensible via props without modification
 * - Liskov Substitution: Can be used anywhere a map is needed
 * - Interface Segregation: Uses focused interfaces
 * - Dependency Inversion: Depends on abstractions (props interfaces)
 */
export const ReusableMap: React.FC<ReusableMapProps> = ({
  markers,
  center,
  zoom = DEFAULT_ZOOM,
  height = DEFAULT_HEIGHT,
  width = '100%',
  borderRadius = borders.radius['2xl'],
  onMarkerClick,
  onMapLoad,
  onMapUnmount,
  mapOptions,
  mapId: mapIdProp,
  showOpenMapsButton = true,
  openMapsButtonText = 'Open Maps',
  loadingComponent,
  errorComponent,
  emptyComponent,
  autoFitBounds = true,
  boundsPadding = { top: 50, right: 50, bottom: 50, left: 50 },
  fitBoundsKey,
  preferDenseRegion = false,
}) => {
  const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const previousFitBoundsKeyRef = useRef<string | undefined>(undefined);

  // Memoize pin elements to avoid recreating them on every render
  const pinElementsRef = useRef<Map<string, HTMLElement>>(new Map());

  // Helper to create or get a pin element for AdvancedMarkerElement
  // Uses the ExperienceMarker component to create marker elements
  const getPinElement = useCallback((marker: MapMarker): HTMLElement | null => {
    // Create cache key that includes price and types to ensure updates when these change
    const typesKey = marker.types ? marker.types.join(',') : 'no-types';
    const cacheKey = `${marker.id}-${marker.price || 'no-price'}-${typesKey}-${marker.isExperience ? 'exp' : 'loc'}`;

    // Check cache first
    if (pinElementsRef.current.has(cacheKey)) {
      return pinElementsRef.current.get(cacheKey)!;
    }

    // Use ExperienceMarker utility to create the element
    const element = createExperienceMarkerElement(marker);
    if (element) {
      pinElementsRef.current.set(cacheKey, element);
    }
    return element;
  }, []);

  // Load Google Maps API using shared loader
  const { isLoaded, loadError, apiKey } = useGoogleMapsLoader();
  const hasApiKey =
    apiKey && apiKey !== '' && apiKey !== 'your_google_maps_api_key_here';

  // Filter valid markers
  const validMarkers = useMemo(() => {
    const filtered = MapBoundsCalculator.filterValidMarkers(markers);
    console.log('🗺️ ReusableMap - Total markers:', markers.length);
    console.log('🗺️ ReusableMap - Valid markers:', filtered.length);
    if (filtered.length > 0) {
      console.log('🗺️ ReusableMap - First marker:', filtered[0]);
      console.log(
        '🗺️ ReusableMap - Last marker:',
        filtered[filtered.length - 1]
      );
    }
    return filtered;
  }, [markers]);

  // When preferDenseRegion (small/compact map), fit to where most markers are; otherwise fit all
  const markersForBounds = useMemo(() => {
    if (!preferDenseRegion || validMarkers.length <= 2) return validMarkers;
    return MapBoundsCalculator.getMarkersForDenseRegion(validMarkers, 0.5);
  }, [preferDenseRegion, validMarkers]);

  // Calculate center from markers if not provided
  // When autoFitBounds is true, center is just initial - fitBounds will override it
  const mapCenter = useMemo(() => {
    if (center) return center;
    if (validMarkers.length > 0) {
      const calculatedCenter =
        MapBoundsCalculator.calculateCenter(validMarkers);
      if (calculatedCenter) {
        console.log('🗺️ ReusableMap - Calculated center:', calculatedCenter);
        return calculatedCenter;
      }
    }
    return DEFAULT_CENTER;
  }, [center, validMarkers]);

  // Calculate initial zoom - will be overridden by fitBounds if autoFitBounds is true
  // Use a wider initial zoom to ensure all markers are visible before fitBounds adjusts
  const initialZoom = useMemo(() => {
    if (!autoFitBounds) return zoom;
    // When autoFitBounds is enabled, use a moderate default zoom that will be adjusted
    // Start with a reasonable zoom level that will be fine-tuned by fitBounds
    if (validMarkers.length === 0) return zoom;
    if (validMarkers.length === 1) return 12;
    // For multiple markers, use zoom 10 instead of 6 to start closer
    // fitBounds will adjust this to perfectly fit all markers
    return 10;
  }, [autoFitBounds, zoom, validMarkers.length]);

  // Container style
  const containerStyle = useMemo(
    () => ({
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      height,
      borderRadius,
      overflow: 'hidden' as const,
      boxSizing: 'border-box' as const,
    }),
    [height, borderRadius]
  );

  // Get Map ID from props (preferred) or environment variable
  // Map ID is required for Advanced Markers (replacement for deprecated Marker)
  // Create a Map ID in Google Cloud Console: https://console.cloud.google.com/google/maps-apis
  const mapId = useMemo(() => {
    // Priority: 1. Props, 2. Environment variables (check multiple possible names)
    const googleMapId =
      mapIdProp ||
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || // Matches .env file variable name
      process.env.NEXT_PUBLIC_GOOGLE_MAP_ID;
    const mapIdEnv = process.env.NEXT_PUBLIC_MAP_ID;

    const id = googleMapId || mapIdEnv || undefined;

    // Ensure it's a valid non-empty string
    const validMapId =
      id && typeof id === 'string' && id.trim().length > 0
        ? id.trim()
        : undefined;

    return validMapId;
  }, [mapIdProp]);

  // Merge map options - Map ID is required for Advanced Markers
  const mergedMapOptions = useMemo(() => {
    // If mapId is present, remove styles as they conflict with cloud-based styling
    // Styles should be managed in Google Cloud Console when using mapId
    const baseOptions = mapId
      ? { ...defaultMapOptions, styles: undefined }
      : defaultMapOptions;

    const options = {
      ...baseOptions,
      ...mapOptions,
    };

    // Always include mapId if available - required for Advanced Markers
    if (mapId) {
      options.mapId = mapId;
    }

    return options;
  }, [mapOptions, mapId]);

  // Handle map load
  const handleMapLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance);

      // Auto-fit bounds if enabled - use markersForBounds (all markers or dense region when compact)
      if (autoFitBounds && markersForBounds.length > 0) {
        console.log(
          '🗺️ ReusableMap - Auto-fitting bounds for',
          markersForBounds.length,
          'markers',
          preferDenseRegion ? '(dense region)' : ''
        );

        // Use requestAnimationFrame to ensure map is fully rendered
        requestAnimationFrame(() => {
          try {
            const bounds = new window.google.maps.LatLngBounds();
            markersForBounds.forEach(marker => {
              if (
                typeof marker.latitude === 'number' &&
                typeof marker.longitude === 'number' &&
                !isNaN(marker.latitude) &&
                !isNaN(marker.longitude)
              ) {
                bounds.extend(
                  new window.google.maps.LatLng(
                    marker.latitude,
                    marker.longitude
                  )
                );
              }
            });

            // Check if we have valid bounds
            if (bounds.isEmpty()) {
              console.warn(
                '🗺️ ReusableMap - Bounds are empty on initial load, skipping fitBounds'
              );
              return;
            }

            // Check if bounds are valid (not all points at same location)
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latDiff = Math.abs(ne.lat() - sw.lat());
            const lngDiff = Math.abs(ne.lng() - sw.lng());

            console.log('🗺️ ReusableMap - Initial bounds:', {
              north: ne.lat(),
              south: sw.lat(),
              east: ne.lng(),
              west: sw.lng(),
              latDiff,
              lngDiff,
              markerCount: markersForBounds.length,
            });

            if (latDiff < 0.0001 && lngDiff < 0.0001) {
              // All markers at same location - center on it with reasonable zoom
              console.log(
                '🗺️ ReusableMap - All markers at same location, centering with zoom 14'
              );
              const center =
                MapBoundsCalculator.calculateCenter(markersForBounds);
              if (center) {
                mapInstance.setCenter(center);
                mapInstance.setZoom(14);
              }
            } else {
              // Multiple distinct locations - fit bounds with padding to show all markers
              // Trigger resize so fitBounds uses correct container size (avoids wrong zoom when layout is not final)
              try {
                window.google.maps.event.trigger(mapInstance, 'resize');
              } catch {
                /* ignore */
              }
              console.log(
                '🗺️ ReusableMap - Fitting bounds with padding:',
                boundsPadding
              );
              mapInstance.fitBounds(bounds, boundsPadding);

              // After fitBounds, only clamp maximum zoom (don't clamp minimum - two far-apart markers need low zoom to both be visible)
              setTimeout(() => {
                const currentZoom = mapInstance.getZoom();
                console.log(
                  '🗺️ ReusableMap - Current zoom after fitBounds:',
                  currentZoom
                );

                if (currentZoom != null && currentZoom > 18) {
                  console.log('🗺️ ReusableMap - Zoom too high, setting to 18');
                  mapInstance.setZoom(18);
                }
                // Do NOT clamp minimum zoom: fitBounds may choose zoom 1–2 for far-apart markers; clamping to 3 would zoom in and push both off viewport
              }, 200); // Increased timeout to ensure map is fully rendered
            }
          } catch (error) {
            console.error(
              '🗺️ ReusableMap - Error fitting initial bounds:',
              error
            );
          }
        });
      }

      onMapLoad?.(mapInstance);
    },
    [
      autoFitBounds,
      markersForBounds,
      boundsPadding,
      preferDenseRegion,
      onMapLoad,
    ]
  );

  // Handle map unmount
  const handleMapUnmount = useCallback(() => {
    setMap(null);
    onMapUnmount?.();
  }, [onMapUnmount]);

  // Track previous bounds to avoid unnecessary updates
  const previousBoundsRef = useRef<{
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
    markerCount: number;
  } | null>(null);

  // Debounce timer ref for bounds updates
  const boundsUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to check if bounds have changed significantly
  const hasBoundsChanged = useCallback(
    (newBounds: google.maps.LatLngBounds, newMarkerCount: number): boolean => {
      const prev = previousBoundsRef.current;

      // If no previous bounds or marker count changed, consider it changed
      if (!prev || prev.markerCount !== newMarkerCount) {
        return true;
      }

      const ne = newBounds.getNorthEast();
      const sw = newBounds.getSouthWest();

      // Calculate if bounds changed significantly (more than 5% difference)
      const latDiff = Math.abs(ne.lat() - sw.lat());
      const lngDiff = Math.abs(ne.lng() - sw.lng());
      const prevLatDiff = Math.abs(prev.ne.lat - prev.sw.lat);
      const prevLngDiff = Math.abs(prev.ne.lng - prev.sw.lng);

      // Check if center moved significantly (more than 10% of bounds size)
      const centerLat = (ne.lat() + sw.lat()) / 2;
      const centerLng = (ne.lng() + sw.lng()) / 2;
      const prevCenterLat = (prev.ne.lat + prev.sw.lat) / 2;
      const prevCenterLng = (prev.ne.lng + prev.sw.lng) / 2;

      const centerLatDiff = Math.abs(centerLat - prevCenterLat);
      const centerLngDiff = Math.abs(centerLng - prevCenterLng);

      const significantChange =
        Math.abs(latDiff - prevLatDiff) /
          Math.max(latDiff, prevLatDiff, 0.001) >
          0.05 ||
        Math.abs(lngDiff - prevLngDiff) /
          Math.max(lngDiff, prevLngDiff, 0.001) >
          0.05 ||
        centerLatDiff > Math.max(latDiff * 0.1, 0.01) ||
        centerLngDiff > Math.max(lngDiff * 0.1, 0.01);

      return significantChange;
    },
    []
  );

  // Update bounds when markers change - use markersForBounds (all or dense region when compact)
  useEffect(() => {
    if (!map || !autoFitBounds || markersForBounds.length === 0) {
      previousBoundsRef.current = null;
      return;
    }

    // When fitBoundsKey changes (e.g. category filter), force refit by clearing previous bounds
    if (fitBoundsKey !== previousFitBoundsKeyRef.current) {
      previousFitBoundsKeyRef.current = fitBoundsKey;
      previousBoundsRef.current = null;
    }

    // Clear any pending updates
    if (boundsUpdateTimerRef.current) {
      clearTimeout(boundsUpdateTimerRef.current);
    }

    // Debounce bounds updates to avoid rapid-fire viewport changes
    boundsUpdateTimerRef.current = setTimeout(() => {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        markersForBounds.forEach(marker => {
          if (
            typeof marker.latitude === 'number' &&
            typeof marker.longitude === 'number' &&
            !isNaN(marker.latitude) &&
            !isNaN(marker.longitude)
          ) {
            bounds.extend(
              new window.google.maps.LatLng(marker.latitude, marker.longitude)
            );
          }
        });

        // Check if we have valid bounds
        if (bounds.isEmpty()) {
          console.warn('🗺️ ReusableMap - Bounds are empty, skipping fitBounds');
          return;
        }

        // Check if bounds have changed significantly before updating
        if (!hasBoundsChanged(bounds, markersForBounds.length)) {
          console.log(
            '🗺️ ReusableMap - Bounds unchanged, skipping fitBounds update'
          );
          return;
        }

        // Store new bounds for comparison
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        previousBoundsRef.current = {
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
          markerCount: markersForBounds.length,
        };

        // Check if bounds are valid
        const latDiff = Math.abs(ne.lat() - sw.lat());
        const lngDiff = Math.abs(ne.lng() - sw.lng());

        if (latDiff < 0.0001 && lngDiff < 0.0001) {
          // All markers at same location
          console.log('🗺️ ReusableMap - All markers at same location (update)');
          const center = MapBoundsCalculator.calculateCenter(markersForBounds);
          if (center) {
            map.setCenter(center);
            map.setZoom(14);
          }
        } else {
          // Multiple distinct locations - fit bounds to show all markers
          try {
            window.google.maps.event.trigger(map, 'resize');
          } catch {
            /* ignore */
          }
          console.log('🗺️ ReusableMap - Fitting bounds (update)', {
            markerCount: markersForBounds.length,
            bounds: {
              north: ne.lat(),
              south: sw.lat(),
              east: ne.lng(),
              west: sw.lng(),
            },
          });
          map.fitBounds(bounds, boundsPadding);

          // Only clamp maximum zoom; do not clamp minimum so far-apart markers stay in viewport
          setTimeout(() => {
            const currentZoom = map.getZoom();
            if (currentZoom != null && currentZoom > 18) {
              console.log('🗺️ ReusableMap - Zoom too high, setting to 18');
              map.setZoom(18);
            }
          }, 200);
        }
      } catch (error) {
        console.error('🗺️ ReusableMap - Error updating bounds:', error);
      }
    }, 300); // 300ms debounce to batch rapid marker changes

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (boundsUpdateTimerRef.current) {
        clearTimeout(boundsUpdateTimerRef.current);
      }
    };
  }, [
    map,
    autoFitBounds,
    markersForBounds,
    boundsPadding,
    hasBoundsChanged,
    fitBoundsKey,
  ]);

  // Handle marker click
  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      // For experience markers, navigate directly without showing InfoWindow
      if (marker.isExperience) {
        // If marker has onClick handler, use it; otherwise use onMarkerClick callback
        if (marker.onClick) {
          marker.onClick();
        } else {
          onMarkerClick?.(marker);
        }
        return;
      }
      // For location markers (non-experience), trigger onMarkerClick to open place details
      // Don't show InfoWindow popup
      onMarkerClick?.(marker);
    },
    [onMarkerClick]
  );

  // Handle open maps button click
  const handleOpenMaps = useCallback(() => {
    if (validMarkers.length > 0) {
      const firstMarker = validMarkers[0];
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${firstMarker.latitude},${firstMarker.longitude}`;
      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    }
  }, [validMarkers]);

  // Show API key error
  if (!hasApiKey) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF9F6', // Warm muted beige matching Figma design
          borderRadius,
          padding: spacing[6],
          textAlign: 'center',
        }}
      >
        <svg
          width='64'
          height='64'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{ marginBottom: spacing[4], opacity: 0.5 }}
        >
          <path
            d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
            fill={typographyColors.tertiary}
          />
        </svg>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            marginBottom: spacing[2],
          }}
        >
          Google Maps API Key Required
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
          }}
        >
          Please configure NEXT_PUBLIC_MAP_API_KEY in your environment
          variables.
        </p>
      </div>
    );
  }

  // Show Map ID error - required for Advanced Markers
  // Only show error if we have markers to display (Advanced Markers are being used)
  if (!mapId && isLoaded && validMarkers.length > 0) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF9F6', // Warm muted beige matching Figma design
          borderRadius,
          padding: spacing[6],
          textAlign: 'center',
        }}
      >
        <svg
          width='64'
          height='64'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{ marginBottom: spacing[4], opacity: 0.5 }}
        >
          <path
            d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
            fill={typographyColors.tertiary}
          />
        </svg>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            marginBottom: spacing[2],
          }}
        >
          Google Maps Map ID Required
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
            marginBottom: spacing[2],
          }}
        >
          Advanced Markers require a Map ID to function properly.
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
            marginBottom: spacing[2],
          }}
        >
          Please configure NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
          NEXT_PUBLIC_GOOGLE_MAP_ID, or NEXT_PUBLIC_MAP_ID in your environment
          variables. Create a Map ID at:{' '}
          <a
            href='https://console.cloud.google.com/google/maps-apis'
            target='_blank'
            rel='noopener noreferrer'
            style={{
              color: typographyColors.primary,
              textDecoration: 'underline',
            }}
          >
            Google Cloud Console
          </a>
        </p>
        <div
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.xs,
            fontWeight: typography.weight.regular,
            color: typographyColors.tertiary,
            backgroundColor: '#F5F5F4',
            padding: spacing[3],
            borderRadius: borders.radius.md,
            marginTop: spacing[2],
          }}
        >
          <strong>Quick Fix:</strong>
          <ol style={{ margin: spacing[2], paddingLeft: spacing[4] }}>
            <li>
              Add to <code>.env.local</code>:{' '}
              <code>NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID=your_map_id</code>
            </li>
            <li>
              Restart your Next.js dev server (required for NEXT_PUBLIC_* vars)
            </li>
            <li>
              Or pass <code>mapId</code> prop directly to ReusableMap component
            </li>
          </ol>
        </div>
        <div
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.xs,
            fontWeight: typography.weight.regular,
            color: typographyColors.tertiary,
            backgroundColor: '#F5F5F4',
            padding: spacing[3],
            borderRadius: borders.radius.md,
            marginTop: spacing[2],
          }}
        >
          <strong>Quick Fix:</strong>
          <ol style={{ margin: spacing[2], paddingLeft: spacing[4] }}>
            <li>
              Add to <code>.env.local</code>:{' '}
              <code>NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID=your_map_id</code>
            </li>
            <li>
              Restart your Next.js dev server (required for NEXT_PUBLIC_* vars)
            </li>
            <li>
              Or pass <code>mapId</code> prop directly to ReusableMap component
            </li>
          </ol>
        </div>
      </div>
    );
  }

  // Show load error
  if (loadError) {
    if (errorComponent) return <>{errorComponent}</>;
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF9F6', // Warm muted beige matching Figma design
          borderRadius,
          fontFamily: fontPrimary,
          fontSize: typography.size.base,
          color: typographyColors.secondary,
        }}
      >
        Error loading Google Maps. Please check your API key.
      </div>
    );
  }

  // Show loading state
  if (!isLoaded) {
    if (loadingComponent) return <>{loadingComponent}</>;
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF9F6', // Warm muted beige matching Figma design
          borderRadius,
          fontFamily: fontPrimary,
          fontSize: typography.size.base,
          color: typographyColors.secondary,
        }}
      >
        Loading map...
      </div>
    );
  }

  // Show empty state
  if (validMarkers.length === 0) {
    if (emptyComponent) return <>{emptyComponent}</>;
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FAF9F6', // Warm muted beige matching Figma design
          borderRadius,
          padding: spacing[6],
          textAlign: 'center',
        }}
      >
        <svg
          width='64'
          height='64'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{ marginBottom: spacing[4], opacity: 0.5 }}
        >
          <path
            d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
            fill={typographyColors.tertiary}
          />
        </svg>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.lg,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            marginBottom: spacing[2],
          }}
        >
          No Places to Display
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
          }}
        >
          Search for a destination to see places on the map
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width,
        maxWidth: '100%',
        minWidth: 0,
        height,
        borderRadius,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={initialZoom}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}
        options={mergedMapOptions}
      >
        {isLoaded &&
          validMarkers.map((marker, index) => {
            // Validate marker position before rendering
            if (
              !marker.latitude ||
              !marker.longitude ||
              isNaN(marker.latitude) ||
              isNaN(marker.longitude)
            ) {
              console.warn('🗺️ ReusableMap - Skipping invalid marker:', marker);
              return null;
            }

            // Get or create pin element for AdvancedMarkerElement
            const pinElement = getPinElement(marker);

            // Don't render if pin element couldn't be created (SSR)
            if (!pinElement) {
              console.warn(
                '🗺️ ReusableMap - No pin element for marker:',
                marker.id
              );
              return null;
            }

            // Log first and last markers being rendered
            if (index === 0 || index === validMarkers.length - 1) {
              console.log(
                `🗺️ ReusableMap - Rendering marker ${index + 1}/${validMarkers.length}:`,
                {
                  id: marker.id,
                  title: marker.title,
                  lat: marker.latitude,
                  lng: marker.longitude,
                }
              );
            }

            return (
              <AdvancedMarker
                key={`${marker.id}-${index}`}
                map={map}
                position={{
                  lat: Number(marker.latitude),
                  lng: Number(marker.longitude),
                }}
                onClick={() => handleMarkerClick(marker)}
                title={marker.title}
                content={pinElement}
                zIndex={1000}
                gmpClickable={true}
              />
            );
          })}

        {selectedMarker && (
          <InfoWindow
            position={{
              lat: selectedMarker.latitude,
              lng: selectedMarker.longitude,
            }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div
              style={{
                minWidth: '200px',
                maxWidth: '300px',
                fontFamily: fontPrimary,
              }}
            >
              {selectedMarker.image && (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element -- dynamic marker image */}
                  <img
                    src={selectedMarker.image}
                    alt={selectedMarker.title}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                      borderRadius: borders.radius.md,
                      marginBottom: spacing[2],
                    }}
                  />
                </>
              )}
              <h3
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.base,
                  fontWeight: typography.weight.semibold,
                  color: typographyColors.primary,
                  marginBottom: spacing[1],
                }}
              >
                {selectedMarker.title}
              </h3>
              {selectedMarker.address && (
                <p
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.sm,
                    fontWeight: typography.weight.regular,
                    color: typographyColors.secondary,
                    marginBottom: spacing[1],
                  }}
                >
                  {selectedMarker.address}
                </p>
              )}
              {selectedMarker.rating && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing[1],
                  }}
                >
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M10 2L12.09 7.26L18 8.27L14 12.14L14.91 18.02L10 15.77L5.09 18.02L6 12.14L2 8.27L7.91 7.26L10 2Z'
                      fill='#FFD700'
                      stroke='#FFD700'
                      strokeWidth='1'
                    />
                  </svg>
                  <span
                    style={{
                      fontFamily: fontPrimary,
                      fontSize: typography.size.sm,
                      fontWeight: typography.weight.semibold,
                      color: typographyColors.primary,
                    }}
                  >
                    {selectedMarker.rating.toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Open Maps Button */}
      {showOpenMapsButton && (
        <button
          onClick={handleOpenMaps}
          style={{
            position: 'absolute',
            bottom: spacing[4],
            right: spacing[4],
            backgroundColor: backgroundColors.primary,
            border: `1px solid ${strokeColors.light}`,
            borderRadius: borders.radius.xl,
            padding: spacing[2],
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '4px 4px 12px 0px rgba(0,0,0,0.12)',
            transition: 'all 0.2s',
            zIndex: 1000,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow =
              '4px 6px 16px 0px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow =
              '4px 4px 12px 0px rgba(0,0,0,0.12)';
          }}
        >
          {/* Google Maps Icon */}
          <div
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width='32'
              height='32'
              viewBox='0 0 32 32'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M16 2C10.48 2 6 6.48 6 12C6 18 16 30 16 30C16 30 26 18 26 12C26 6.48 21.52 2 16 2Z'
                fill='#EA4335'
              />
              <circle cx='16' cy='12' r='4' fill='#FFFFFF' />
            </svg>
          </div>

          {/* Open Maps Text */}
          <p
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.semibold,
              color: typographyColors.primary,
              lineHeight: 'normal',
              margin: 0,
            }}
          >
            {openMapsButtonText}
          </p>

          {/* Arrow Up Right Icon */}
          <div
            style={{
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M5.83333 5.83333H14.1667V14.1667'
                stroke={typographyColors.primary}
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M14.1667 5.83333L5.83333 14.1667'
                stroke={typographyColors.primary}
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </button>
      )}
    </div>
  );
};
